import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Group } from '@microsoft/microsoft-graph-types-beta';
import { select, Store } from '@ngrx/store';
import { map, Observable, pairwise, skipUntil, startWith } from 'rxjs';
import { BaseComponent } from '../../../base/base.component';
import {
    BindingDialogComponent,
    BindingDialogData,
    BindingDialogResult,
} from './binding-dialog/binding-dialog.component';
import { initAuthenticationMethodsCertificateBasedForm } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/certificate-based/form';
import { CertificateBasedAuthenticationMethodConfigurationForm } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/certificate-based/model';
import { updateAuthenticationMethodsCertificateBasedSchema } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/certificate-based/schema';
import { client } from 'projects/angular-clarity/src/app/stores/client';

@Component({
    selector: 'app-baseline',
    templateUrl: './baseline.component.html',
    styleUrls: ['./baseline.component.scss', '../../../styles/baseline-style.scss'],
})
export class BaselineComponent extends BaseComponent implements OnInit {
    groups$: Observable<Map<string, Group>>;

    constructor(
        private store: Store<any>,
        private dialog: MatDialog,
    ) {
        super();
    }

    ngOnInit(): void {
        this.groups$ = this.store
            .pipe(select(client(this.tenant_id).graph.groups.all))
            .pipe(skipUntil(this.store.pipe(select(client(this.tenant_id).graph.groups.loaded))))
            .pipe(
                map((groups) => {
                    return new Map(groups.map((group) => [group.id, group]));
                }),
            );

        const schema = JSON.parse(JSON.stringify(this.baseline.schema));
        this.form = initAuthenticationMethodsCertificateBasedForm(schema);

        // Trigger initial validation so affinity errors show on load
        this.form.updateValueAndValidity();

        // Listen for state changes from disabled to enabled
        this.form
            .get('state')
            ?.valueChanges.pipe(startWith(this.form.get('state')?.value), pairwise())
            .subscribe(([previousState, currentState]) => {
                if (previousState === 'disabled' && currentState === 'enabled') {
                    this.setDefaultValuesIfNeeded();
                }
            });

        this.form.valueChanges.subscribe((_) => {
            const form = this.formValue;
            const updatedSchema = updateAuthenticationMethodsCertificateBasedSchema(form);

            if (this.form.invalid) {
                this.baselineErrorChange.next({ remediate: true, save: true });
            } else {
                this.baselineErrorChange.next({ remediate: false, save: false });
            }

            this.baselineChange.next({
                ...this.baseline,
                schema: JSON.parse(JSON.stringify(updatedSchema)),
            });
        });
    }

    private setDefaultValuesIfNeeded(): void {
        const includeTargetsGroup = this.form.get('includeTargets') as FormGroup;
        const includedGroupsArray = includeTargetsGroup.get('includedGroups') as FormArray;

        if (!includedGroupsArray || includedGroupsArray.length === 0) {
            includeTargetsGroup.get('groupInclusion')?.setValue(false);
            includedGroupsArray.clear();
            const allUsersGroup = new FormGroup({
                id: new FormControl('all_users'),
            });
            includedGroupsArray.push(allUsersGroup);
        }

        const excludeTargets = this.form.get('excludeTargets')?.value;
        if (!excludeTargets || excludeTargets.length === 0) {
            this.form.get('excludeTargets')?.setValue([]);
        }
    }

    get bindingsFormArray(): FormArray {
        return this.form.get('certificateUserBindings') as FormArray;
    }

    get usedCertFields(): string[] {
        return this.bindingsFormArray.controls.map((ctrl) => ctrl.get('x509CertificateField')?.value).filter(Boolean);
    }

    openAddBindingDialog(): void {
        const requiredAffinity = this.form.get(
            'authenticationModeConfiguration.x509CertificateDefaultRequiredAffinityLevel',
        )?.value;
        const dialogRef = this.dialog.open<BindingDialogComponent, BindingDialogData, BindingDialogResult>(
            BindingDialogComponent,
            {
                width: '480px',
                data: {
                    usedCertFields: this.usedCertFields,
                    requiredAffinity: requiredAffinity,
                },
            },
        );

        dialogRef.afterClosed().subscribe((result) => {
            if (!result) return;
            const nextPriority = this.bindingsFormArray.length + 1;
            const newGroup = new FormGroup({
                x509CertificateField: new FormControl(result.x509CertificateField),
                userProperty: new FormControl(result.userProperty),
                priority: new FormControl(nextPriority),
                trustAffinityLevel: new FormControl(result.trustAffinityLevel),
            });
            this.bindingsFormArray.push(newGroup);
        });
    }

    openEditBindingDialog(index: number): void {
        const ctrl = this.bindingsFormArray.at(index) as FormGroup;
        const currentBinding = ctrl.getRawValue();
        const requiredAffinity = this.form.get(
            'authenticationModeConfiguration.x509CertificateDefaultRequiredAffinityLevel',
        )?.value;

        const dialogRef = this.dialog.open<BindingDialogComponent, BindingDialogData, BindingDialogResult>(
            BindingDialogComponent,
            {
                width: '480px',
                data: {
                    binding: currentBinding,
                    usedCertFields: this.usedCertFields,
                    editIndex: index,
                    requiredAffinity: requiredAffinity,
                },
            },
        );

        dialogRef.afterClosed().subscribe((result) => {
            if (!result) return;
            ctrl.patchValue({
                x509CertificateField: result.x509CertificateField,
                userProperty: result.userProperty,
                trustAffinityLevel: result.trustAffinityLevel,
            });
        });
    }

    removeBinding(index: number): void {
        this.bindingsFormArray.removeAt(index);
        this.bindingsFormArray.controls.forEach((ctrl, i) => {
            ctrl.get('priority')?.setValue(i + 1);
        });
    }

    get formValue(): CertificateBasedAuthenticationMethodConfigurationForm {
        return this.form.getRawValue();
    }
}
